import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import useBetaFeatures from "hooks/useBetaFeatures";
import useWebUiToggle from "hooks/useWebUiToggle";
import { effectiveWebUi, readUiPreference } from "lib/uiPreference";

const DISMISSED_KEY_PREFIX = "new-ui-banner:dismissed:";
const SESSION_DISMISSED_KEY_PREFIX = "new-ui-banner:session-dismissed:";
export const BANNER_HEIGHT_CSS_VAR = "--new-ui-banner-height";

/**
 * "Conocé la nueva DrApp" — aparece cuando Igor habilitó el team pero este
 * usuario todavía no optó (UI_REFACTOR_PLAN.md §7: webUiAvailable && !webUi).
 * Dismiss permanente en localStorage, igual criterio que el closedAt de EHR
 * v2 (App/EHR/V2/EHR.js) pero sin snooze — acá no hay un "volvé a preguntar
 * en 30 días", es simplemente "no me interesa por ahora" (para volver a
 * verlo más tarde, o para optar directamente, está el toggle del menú de
 * cuenta — ver Navbar.js).
 *
 * Bug real (@nachoconstenla, 2026-08-26): este div vivía en el flujo normal
 * del documento, después de todo el contenido de la ruta actual — en
 * pantallas largas quedaba varios scrolls abajo (inalcanzable sin saber que
 * había que buscarlo), y en pantallas cortas flotaba a mitad de la ventana.
 * `position-fixed bottom-0` lo saca del flujo del documento — siempre
 * pegado al borde inferior del viewport, sin depender de cuánto contenido
 * tenga la ruta activa.
 *
 * Bug real (Nacho, 2026-09-09): con `justify-content-between` el grupo de
 * botones queda pegado al borde derecho de la ventana, donde el widget de
 * HubSpot (App.js, hs-script-loader) dibuja su burbuja flotante en desktop
 * — tapaba "Ahora no".
 *
 * Bug real #2 (Nacho, 2026-09-09): en mobile la burbuja de HubSpot se ubica
 * del lado IZQUIERDO — mover todo el contenido a la izquierda (fix de
 * arriba) resolvía desktop pero tapaba "Probarla" en mobile. No hay un solo
 * lado seguro en las 2 resoluciones: mobile agrupa a la derecha
 * (`justify-content-end`), desktop (`lg` y superior) a la izquierda
 * (`justify-content-lg-start`).
 */
export default function NewUiBanner({ teamId }) {
  const { t } = useTranslation();
  const features = useBetaFeatures();
  const dismissKey = `${DISMISSED_KEY_PREFIX}${teamId}`;
  const [dismissed, setDismissed] = useState(() => {
    try {
      return window.localStorage.getItem(dismissKey) === "1";
    } catch {
      return false;
    }
  });
  // Bug real (Nacho, 2026-09-09, encontrado revisando la zona): con el
  // override de sesión activo, `visible` (abajo) ignora `dismissed` a
  // propósito — pero eso volvía "Ahora no" un botón MUERTO durante todo el
  // override: el click persistía el dismiss permanente igual, sin ningún
  // cambio visible en la UI. Bajo override, el dismiss usa este flag de
  // SESIÓN aparte — real para el click de ahora, nunca permanente (una
  // sesión nueva — sessionStorage se limpia sola al cerrar la tab — vuelve
  // a mostrarlo, que es justo el comportamiento pedido).
  const sessionDismissKey = `${SESSION_DISMISSED_KEY_PREFIX}${teamId}`;
  const [sessionDismissed, setSessionDismissed] = useState(() => {
    try {
      return window.sessionStorage.getItem(sessionDismissKey) === "1";
    } catch {
      return false;
    }
  });
  const { setWebUi, isLoading } = useWebUiToggle(teamId);
  const legacyOverrideActive = readUiPreference() === "legacy";

  // Bug real (QA-Cormos, 2026-09-02): mirar `features.webUi` a secas acá
  // hacía que este banner desapareciera si el usuario volvía de `web` con
  // `?ui=legacy` (el flag real sigue en true tras el primer opt-in, aunque
  // esté viendo drmanager por el override de sesión) — ver uiPreference.js.
  //
  // Bug real (Nacho, 2026-09-09): el dismiss es PERMANENTE (localStorage,
  // sin expirar) y global a cualquier ruta — un solo click en "Ahora no",
  // en cualquier momento, apagaba el banner para siempre, incluso al
  // volver más tarde con el override explícito `?ui=legacy`. Ese override
  // es una señal puntual y deliberada ("estoy yendo y viniendo entre las 2
  // UI ahora mismo", típicamente un link de "Volver a la anterior" desde
  // `web`) — pesa más que un dismiss viejo, que sólo debería aplicar al
  // camino "legacy orgánico" (nunca optó, sin override de sesión activo).
  const visible =
    features.webUiAvailable &&
    !effectiveWebUi(features.webUi) &&
    !(legacyOverrideActive ? sessionDismissed : dismissed);

  // Bug real (regresión encontrada arreglando el bug de arriba, 2026-09-09):
  // este banner es position-fixed bottom-0, y ahora que puede quedar visible
  // de forma permanente (override ?ui=legacy activo), tapa contenido de
  // popups/modales que antes asumían tener todo el viewport disponible —
  // en un celular angosto llegaba a esconder por completo los botones de
  // guardar/opciones del form de turno, sin ninguna forma de scrollear para
  // alcanzarlos (videocall.spec.js, regresión real detectada en @regression).
  // Se expone la altura real del banner como variable CSS en <html> — igual
  // patrón que --controls-height en meet — para que cualquier modal pueda
  // reservarle espacio con `calc(100vh - var(--new-ui-banner-height, 0px))`
  // en vez de un número fijo adivinado (el texto puede envolver a 2 líneas
  // en pantallas angostas, cambiando la altura real).
  const bannerRef = useRef(null);
  useEffect(() => {
    if (!visible) {
      document.documentElement.style.removeProperty(BANNER_HEIGHT_CSS_VAR);
      return;
    }
    const setHeight = () => {
      if (bannerRef.current) {
        document.documentElement.style.setProperty(BANNER_HEIGHT_CSS_VAR, `${bannerRef.current.offsetHeight}px`);
      }
    };
    setHeight();
    window.addEventListener("resize", setHeight);
    // Bug real (encontrado reproduciendo contra QA real, no en tests unit):
    // en la carga inicial de la página, este efecto puede correr ANTES de
    // que el CSS externo (Bootstrap) termine de aplicarse — offsetHeight
    // medía 0 en ese instante, y sin nada más que lo vuelva a medir, la
    // variable quedaba pisada en "0px" para siempre (el resize de ventana
    // nunca llega a disparar solo). ResizeObserver sí nota el cambio de 0 a
    // la altura real apenas el layout se asienta, sin importar la causa
    // (CSS tardío, fuente que carga después, texto que envuelve). jsdom no
    // lo implementa — fallback silencioso a solo resize (ya cubierto arriba)
    // en unit tests, donde offsetHeight siempre da 0 de cualquier forma.
    let observer;
    if (typeof ResizeObserver !== "undefined" && bannerRef.current) {
      observer = new ResizeObserver(setHeight);
      observer.observe(bannerRef.current);
    }
    return () => {
      window.removeEventListener("resize", setHeight);
      observer?.disconnect();
      document.documentElement.style.removeProperty(BANNER_HEIGHT_CSS_VAR);
    };
  }, [visible]);

  if (!visible) return null;

  const dismiss = () => {
    if (legacyOverrideActive) {
      setSessionDismissed(true);
      try {
        window.sessionStorage.setItem(sessionDismissKey, "1");
      } catch {}
      return;
    }
    setDismissed(true);
    try {
      window.localStorage.setItem(dismissKey, "1");
    } catch {}
  };

  return (
    <div
      ref={bannerRef}
      className="alert alert-info d-flex flex-wrap gap-3 align-items-center justify-content-end justify-content-lg-start m-0 rounded-0 position-fixed bottom-0 start-0 end-0"
      style={{ zIndex: 1080 }}
    >
      <span>
        {t("new_ui_banner_message", "Conocé la nueva DrApp: más simple, más rápida.")}
      </span>
      <div className="d-flex gap-2">
        <button
          type="button"
          className="btn btn-sm btn-primary"
          disabled={isLoading}
          onClick={() => setWebUi(true)}
        >
          {t("new_ui_banner_cta", "Probarla")}
        </button>
        <button type="button" className="btn btn-sm btn-link text-body" onClick={dismiss}>
          {t("new_ui_banner_dismiss", "Ahora no")}
        </button>
      </div>
    </div>
  );
}
